<template>
  <div class="booking-card">
    <div class="row g-3 align-items-stretch">
      <!-- Booking Image -->
      <div class="col-12 col-md-6">
        <BookingImage :image="booking.image" class="h-100" />
      </div>

      <!-- Booking Details -->
      <div class="col-12 col-md-6">
        <div class="card-body d-flex flex-column pt-0 pb-0 h-100 px-2 px-md-4">
          <!-- Lihe Name and Station Name -->
          <BookingHeader
            :liheName="booking.liheName"
            :stationName="booking.stationName"
            :show-station-header="showStationHeader"
          />
          <!-- Booking Date -->
          <BookingDate
            v-if="showBookingDate"
            class="mt-2"
            :booking-option="booking.options.find(option => option.prestTypeId === 1) || {
              startDate: booking.departureDate,
              endDate: booking.returnDate,
              prestTypeId: 1
            }"
          />

          <!-- Prestation Information -->
          <PrestationInfo :options="booking.options" v-if="showPrestationInfo"/>

          <!-- Iresa Reference Component -->
          <IresaReference :reference="booking.reference" />

          <!-- Pushed to the bottom -->
          <div v-if="!isPanelView && isReservationStatus" class="mt-auto">
            <!-- Participant Info Component -->
            <MissingTravellerInfoWarning
              v-if="missingTravellerInfo"
              :booking="booking"
            />

            <div class="d-flex gap-2 flex-column flex-md-row">
              <ViewDetailsButton
                :booking="booking"
                @click="handleViewDetails"
              />
              <ReserveActionsButton
                v-if="showPayBalanceButton"
                :booking="booking"
                :default-label="payActionLabel"
                class="flex-grow-1"
              />
              <PaymentButton
                v-else
                :userId="userId"
                :booking="booking"
                class="flex-grow-1"
              />
            </div>
          </div>

          <div v-if="!isPanelView && isQuoteOrCartInProgress" class="mt-auto">
            <!-- Package Container -->
            <PackageContainer 
              :packageCode="booking.packageCode" 
              :bookingReference="booking.reference || ''"
              v-if="showPackageSection"
            />
            <!-- Price and Button -->
            <PriceAndViewDetailsButton
              :booking="booking"
              :is-reservation="false"
            />
          </div>

          <!-- Panel Action (visible only in Panel View) -->
          <PanelAction
            v-if="isPanelView && showPanelAction"
            :isPastStatus="isPastStatus"
            :booking="booking"
            class="mt-auto"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  /* ------------------- Vue Core & Utilities ------------------- */
  import { defineProps, computed, ComputedRef, defineEmits } from 'vue'

  /* ------------------- Store ------------------- */
  import { useStore } from '@/store'

  /* ------------------- Configurations & Enums ------------------- */
  import { ModalId } from '@/config/modalIds'

  /* ------------------- Types ------------------- */
  import {
    BookingSummary,
    TravellerSummary,
  } from '@/services/BookingService.interface'

  /* ------------------- Components ------------------- */
  import BookingImage from '@/components/common/BookingImage.vue'
  import BookingHeader from '@/components/common/BookingHeader.vue'
  import BookingDate from '@/components/common/BookingDate.vue'
  import PrestationInfo from '@/components/common/PrestationInfo.vue'
  import PackageContainer from '@/components/common/PackageContainer.vue'
  import MissingTravellerInfoWarning from '@/components/common/MissingTravellerInfoWarning.vue'
  import PaymentButton from '@/components/common/PaymentButton.vue'
  import ViewDetailsButton from '@/components/common/ViewDetailsButton.vue'
  import IresaReference from '@/components/common/IresaReference.vue'
  import PanelAction from '@/components/common/PanelAction.vue'
  import PriceAndViewDetailsButton from '@/components/common/PriceAndViewDetailsButton.vue'
  import ReserveActionsButton from '@/components/common/ReserveActionsButton.vue'

  /* ------------------- Composables ------------------- */
  import { useBookingStatus } from '@/composables/useBookingStatus'
  import { useSectionVisibilty } from '@/composables/useSectionVisibilty'
  import { useBookingFunnelActions } from '@/composables/useBookingFunnelActions'
  import { useBookingFunnelNavigation } from '@/composables/useBookingFunnelNavigation'
  /* ------------------- Store & Props ------------------- */
  const store = useStore()

  const props = defineProps<{
    booking: BookingSummary
    isPanelView?: boolean
  }>()

  /* ------------------- Section visibility ------------------- */
  const {showStationHeader, showBookingDate, showPrestationInfo, showPanelAction, showPackageSection} = useSectionVisibilty(props.booking.liheId, props.booking.liheName, props.booking.packageCode)

  /* ------------------- Booking Statuses ------------------- */
  const { isReservationStatus, isQuoteOrCartInProgress, isPastStatus } =
    useBookingStatus(props.booking)

  const { showPayBalanceButton } = useBookingFunnelActions(() => props.booking)
  const { payActionLabel } = useBookingFunnelNavigation(() => props.booking)

  /* ------------------- Computed Properties ------------------- */
  /**
   * Retrieves the user ID from the store.
   */
  const userId: ComputedRef<string | null> = computed(
    () => store.state.auth?.user?.iresa_id || null
  )

  /**
   * Checks if any traveller is missing required information.
   */
  const missingTravellerInfo: ComputedRef<boolean> = computed(() =>
    props.booking.travellers.some(
      (traveller: TravellerSummary) =>
        !traveller.firstName ||
        traveller.firstName.trim() === '' ||
        !traveller.lastName ||
        traveller.lastName.trim() === '' ||
        traveller.firstName === null ||
        traveller.lastName === null
    )
  )

  /* ------------------- Emits ------------------- */
  defineEmits<{
    (event: 'view-details'): void
  }>()

  /* ------------------- Methods ------------------- */
  /**
   * Handles the "View Details" action by opening the booking panel modal.
   */
  const handleViewDetails = (): void => {
    store.commit('modal/open', ModalId.BOOKING_PANEL)
    store.dispatch('booking/selectBooking', props.booking)
  }
</script>

<style scoped>
  .booking-card {
    border-radius: var(--border-radius-default, 8px);
  }

  .action-button {
    flex: 1 1 auto;
    min-width: 100px;
  }

  @media (min-width: 768px) {
    .row {
      align-items: stretch;
    }
  }
</style>
